import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import styled from "styled-components";
import { colors } from "../../assets/colors";
import { BsChevronDoubleDown } from "react-icons/bs";
import { CursorContextProvider, Cursor, useCursorHandlers, useSectionCursor } from "../general/Cursor";
import Nav from "../general/Nav";
import { useAuth } from "../../contexts/AuthContext";
import { 
  getCohortByStartupName, 
  getBlogMessagesByCohortId,
  createBlogMessage,
  updateBlogMessage,
  deleteBlogMessage
} from "./backend/blogService";
import editIcon from "../../assets/edit-white.svg";
import trashIcon from "../../assets/trash-white.svg";

// Load all logos from assets/logos folder at build time
const logoContext = require.context('../../assets/logos', false, /\.png$/);
const logoMap = {};
logoContext.keys().forEach((item) => {
  const logoName = item.replace('./', '').replace('.png', '');
  logoMap[logoName] = logoContext(item);
});

// Helper function to get logo from assets folder
// Format: {startupName}_Logo.png
const getLocalLogo = (startupName) => {
  if (!startupName) return null;
  const logoName = `${startupName.replace(/\s+/g, '')}_Logo`;
  return logoMap[logoName] || null;
};

const BlogPageContainer = styled.div`
  width: 100vw;
  min-height: 100vh;
  background-color: ${colors.white};
  font-family: "ReadexPro", sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  max-width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
`;

const BlogPageContent = styled.div`
  width: 100%;
  max-width: 1200px;
  padding: 10vh 5vw;
  display: flex;
  flex-direction: column;
  gap: 0rem;
  box-sizing: border-box;
`;

const StartupHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.25rem;
  margin-bottom: 1rem;
`;

const StartupLogo = styled.img`
  max-width: 250px;
  max-height: 250px;
  width: 100%;
  object-fit: contain;
`;

const StartupName = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  color: ${colors.black};
  margin: 0;
  @media screen and (max-width: 768px) {
    font-size: 2rem;
  }
`;

const StartupFounders = styled.p`
  font-size: 1.2rem;
  color: ${colors.darkGrayText};
  margin: 0;
`;

const BlogPageHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: relative;
  margin-bottom: 1rem;
`;

const BlogMessagesTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${colors.black};
  margin: 0;
  text-align: center;
`;

// Auth Status Component
const AuthStatusContainer = styled.div`
  position: absolute;
  top: 12vh;
  right: 5vw;
  background-color: ${colors.white};
  border: 2px solid ${colors.gray};
  border-radius: 10px;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
  align-items: center;
  flex-shrink: 0;
  z-index: 10;
`;

const AuthButton = styled.button`
  background-color: ${colors.red};
  color: ${colors.white};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: none;
  transition: all 0.3s ease;
  font-family: ReadexPro, sans-serif;
  align-self: flex-start;
  margin-top: 0;
  
  &:hover {
    background-color: ${colors.darkGray};
    transform: translateY(-2px);
  }
`;


const AuthInfo = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
  align-items: flex-start;
`;

const AuthTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  max-width: 200px;
  min-width: 0;
`;

const AuthText = styled.p`
  font-family: ReadexPro, sans-serif;
  font-size: 0.9rem;
  color: ${colors.black};
  margin: 0;
  font-weight: 500;
  line-height: 1.2;
`;

const AuthEmail = styled.p`
  font-family: ReadexPro, sans-serif;
  font-size: 0.85rem;
  color: ${colors.darkGrayText};
  margin: 0;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const BlogMessagesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-top: 2rem;
`;

const BlogMessageWrapper = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;

const BlogMessageAvatar = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background-color: ${colors.white};
  border: 2px solid ${colors.lightGray || '#f0f0f0'};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: 8px;
  }
`;

const BlogMessageCard = styled.div`
  background-color: ${colors.white};
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
`;

const BlogMessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  width: 100%;
`;

const BlogMessageTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${colors.black};
  margin: 0;
  padding: 0;
  line-height: 1.2;
  display: flex;
  align-items: center;
`;

const BlogMessageDate = styled.span`
  font-size: 0.9rem;
  color: ${colors.darkGrayText};
  white-space: nowrap;
  display: block;
  margin-top: 0.25rem;
`;

const BlogMessageContent = styled.div`
  font-size: 1rem;
  color: ${colors.darkGrayText};
  line-height: 1.6;
  white-space: pre-wrap;
`;

const BlogMessageMeta = styled.div`
  display: flex;
  gap: 1rem;
  font-size: 0.9rem;
  color: ${colors.darkGrayText};
  padding-top: 1rem;
  border-top: 1px solid ${colors.gray};
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${colors.darkGrayText};
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${colors.red};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem 2rem 4rem 2rem;
  color: ${colors.darkGrayText};
  margin-top: -1rem;
`;

const BackLink = styled(Link)`
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
  cursor: pointer;
  text-decoration: none;
`;




// Button Base Component
const ButtonBase = styled.button`
  background-color: ${colors.red};
  color: ${colors.white};
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: none;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${colors.darkGray};
    transform: translateY(-2px);
  }
`;

// Button Variations
const AddPostButton = styled(ButtonBase)`
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  margin-top: 1rem;
  width: 100%;
  max-width: 300px;
  align-self: center;
`;

const IconButton = styled(ButtonBase)`
  padding: 0.5rem;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: 0;
  
  img {
    width: 18px;
    height: 18px;
    display: block;
  }
`;

const EditControls = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  align-items: flex-start;
  flex-shrink: 0;
`;

const EditForm = styled.div`
  background-color: ${colors.white};
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormInput = styled.input`
  padding: 0.75rem;
  border: 2px solid ${colors.gray};
  border-radius: 10px;
  font-size: 1rem;
  font-family: "ReadexPro", sans-serif;
  box-sizing: border-box;
  width: 100%;
  text-indent: 0;
  
  &:focus {
    outline: none;
    border-color: ${colors.red};
  }
`;

const FormTextarea = styled.textarea`
  padding: 0.75rem;
  border: 2px solid ${colors.gray};
  border-radius: 10px;
  font-size: 1rem;
  font-family: "ReadexPro", sans-serif;
  min-height: 200px;
  resize: vertical;
  box-sizing: border-box;
  width: 100%;
  text-indent: 0;
  
  &:focus {
    outline: none;
    border-color: ${colors.red};
  }
`;

const FormButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const CancelButton = styled(ButtonBase)`
  background-color: ${colors.gray};
  color: ${colors.black};
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  margin: 0;
  width: 120px;
  box-sizing: border-box;
  
  &:hover {
    background-color: ${colors.gray};
    opacity: 0.8;
    transform: none;
  }
`;

const SaveButton = styled(ButtonBase)`
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  margin: 0;
  width: 120px;
  box-sizing: border-box;
`;

function BlogInstancePage() {
  return (
    <CursorContextProvider>
      <Cursor />
      <BlogInstancePageInner />
    </CursorContextProvider>
  );
}

function BlogInstancePageInner() {
  const { startupName } = useParams();
  const navigate = useNavigate();
  const { currentUser, signInWithGoogle, logout } = useAuth();
  const sectionCursorHandlers = useSectionCursor({ color: colors.black });
  const cursorHandlers = useCursorHandlers();
  const [startupInfo, setStartupInfo] = useState(null);
  const [cohortEmail, setCohortEmail] = useState(null);
  const [blogMessages, setBlogMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', content: '' });
  const [authLoading, setAuthLoading] = useState(false);
  
  // Authorized if admin (same as blog list) or email matches cohort contact
  const ADMIN_EMAILS = ['team@txmomentum.org', 'bhasin.arnav07@gmail.com'];
  const isAdmin = currentUser && ADMIN_EMAILS.some((e) => currentUser.email?.toLowerCase() === e.toLowerCase());
  const isCohortContact = currentUser && cohortEmail && currentUser.email?.toLowerCase() === cohortEmail.toLowerCase();
  const isAuthorized = currentUser && (isAdmin || isCohortContact);

  const handleSignIn = async () => {
    setAuthLoading(true);
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Sign in error:', error);
      alert('Failed to sign in. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Decode the startup name from URL
        const decodedName = decodeURIComponent(startupName);
        
        // Get the cohort/startup info
        const cohort = await getCohortByStartupName(decodedName);
        
        const startupInfoData = {
          name: cohort.startupName || '',
          founders: cohort.founderNames || '',
          cohortId: cohort.cohortId,
        };
        setStartupInfo(startupInfoData);
        setCohortEmail(cohort.email || null);

        // Fetch blog messages for this cohort using cohortId number
        const messages = await getBlogMessagesByCohortId(cohort.cohortId);
        
        // Map blog messages to display format
        const formattedMessages = messages.map(message => {
          return {
            id: message.id,
            title: message.messageTitle || 'Untitled',
            content: message.messageBody || '',
            date: message.postedDate || new Date(),
          };
        });

        setBlogMessages(formattedMessages);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [startupName]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleAddPost = () => {
    setFormData({ title: '', content: '' });
    setShowAddForm(true);
    setEditingMessage(null);
  };

  const handleEditPost = (message) => {
    setFormData({
      title: message.title,
      content: message.content
    });
    setEditingMessage(message);
    setShowAddForm(true);
  };

  const handleDeletePost = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }
    
    try {
      await deleteBlogMessage(messageId);
      // Refresh messages
      const messages = await getBlogMessagesByCohortId(startupInfo.cohortId);
      const formattedMessages = messages.map(message => ({
        id: message.id,
        title: message.messageTitle || 'Untitled',
        content: message.messageBody || '',
        date: message.postedDate || new Date(),
      }));
      setBlogMessages(formattedMessages);
    } catch (err) {
      console.error('Error deleting message:', err);
      alert('Failed to delete post. Please try again.');
    }
  };

  const handleSavePost = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      alert('Please fill in both title and content.');
      return;
    }

    try {
      if (editingMessage) {
        // Update existing message
        await updateBlogMessage(editingMessage.id, {
          messageTitle: formData.title,
          messageBody: formData.content
        });
      } else {
        // Create new message
        await createBlogMessage({
          messageTitle: formData.title,
          messageBody: formData.content,
          cohortId: startupInfo.cohortId,
          postedDate: new Date(),
          createdBy: currentUser.email
        });
      }

      // Refresh messages
      const messages = await getBlogMessagesByCohortId(startupInfo.cohortId);
      const formattedMessages = messages.map(message => ({
        id: message.id,
        title: message.messageTitle || 'Untitled',
        content: message.messageBody || '',
        date: message.postedDate || new Date(),
      }));
      setBlogMessages(formattedMessages);
      
      // Reset form
      setFormData({ title: '', content: '' });
      setShowAddForm(false);
      setEditingMessage(null);
    } catch (err) {
      console.error('Error saving message:', err);
      alert('Failed to save post. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setFormData({ title: '', content: '' });
    setShowAddForm(false);
    setEditingMessage(null);
  };

  if (loading) {
    return (
      <BlogPageContainer {...sectionCursorHandlers}>
        <BlogPageContent>
          <LoadingContainer>Loading...</LoadingContainer>
        </BlogPageContent>
        <Nav color={0} home={1} />
      </BlogPageContainer>
    );
  }

  if (error) {
    return (
      <BlogPageContainer {...sectionCursorHandlers}>
        <BlogPageContent>
          <ErrorContainer>Error: {error}</ErrorContainer>
        </BlogPageContent>
        <BackLink {...cursorHandlers} onClick={handleBackClick}>
          <BsChevronDoubleDown size={40} color={colors.red} />
        </BackLink>
        <Nav color={0} home={1} />
      </BlogPageContainer>
    );
  }

  if (!startupInfo) {
    return (
      <BlogPageContainer {...sectionCursorHandlers}>
        <BlogPageContent>
          <ErrorContainer>Startup not found</ErrorContainer>
        </BlogPageContent>
        <BackLink {...cursorHandlers} onClick={handleBackClick}>
          <BsChevronDoubleDown size={40} color={colors.red} />
        </BackLink>
        <Nav color={0} home={1} />
      </BlogPageContainer>
    );
  }

  return (
    <BlogPageContainer {...sectionCursorHandlers}>
        <AuthStatusContainer>
          {currentUser ? (
            <AuthInfo>
              <AuthTextContainer>
                <AuthText>Signed in as:</AuthText>
                <AuthEmail>{currentUser.email}</AuthEmail>
              </AuthTextContainer>
              <AuthButton {...cursorHandlers} onClick={handleSignOut}>Sign Out</AuthButton>
            </AuthInfo>
          ) : (
            <AuthInfo>
              <AuthButton {...cursorHandlers} onClick={handleSignIn} disabled={authLoading}>
                {authLoading ? 'Signing in...' : 'Sign in with Google'}
              </AuthButton>
            </AuthInfo>
          )}
        </AuthStatusContainer>
        <BlogPageContent>
          <StartupHeader>
            {(() => {
              const localLogo = getLocalLogo(startupInfo.name);
              return localLogo ? (
                <StartupLogo src={localLogo} alt={`${startupInfo.name} Logo`} />
              ) : null;
            })()}
            {startupInfo.founders && (
              <StartupFounders>By {startupInfo.founders}</StartupFounders>
            )}
          </StartupHeader>

          <BlogPageHeader>
            <BlogMessagesTitle>Blog Posts</BlogMessagesTitle>
          </BlogPageHeader>
          
          {isAuthorized && (
            <AddPostButton {...cursorHandlers} onClick={handleAddPost}>
              {showAddForm && !editingMessage ? 'Cancel' : '+ Add New Post'}
            </AddPostButton>
          )}

          {showAddForm && isAuthorized && (
            <EditForm>
              <FormInput
                type="text"
                placeholder="Post Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
              <FormTextarea
                placeholder="Post Content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              />
              <FormButtons>
                <CancelButton {...cursorHandlers} onClick={handleCancelEdit}>Cancel</CancelButton>
                <SaveButton {...cursorHandlers} onClick={handleSavePost}>Save</SaveButton>
              </FormButtons>
            </EditForm>
          )}
          
          {blogMessages.length === 0 ? (
            <EmptyState>No blog posts yet. Check back soon!</EmptyState>
          ) : (
            <BlogMessagesList>
              {blogMessages.map((message) => (
                <BlogMessageWrapper key={message.id}>
                  {getLocalLogo(startupInfo.name) && (
                    <BlogMessageAvatar>
                      <img src={getLocalLogo(startupInfo.name)} alt={`${startupInfo.name} Logo`} />
                    </BlogMessageAvatar>
                  )}
                  <BlogMessageCard>
                    <BlogMessageHeader>
                      <div style={{ flex: 1 }}>
                        <BlogMessageTitle>{message.title}</BlogMessageTitle>
                        {message.date && (
                          <BlogMessageDate>
                            {new Date(message.date).toLocaleDateString()}
                          </BlogMessageDate>
                        )}
                      </div>
                      {isAuthorized && (
                        <EditControls>
                          <IconButton 
                            {...cursorHandlers}
                            onClick={() => handleEditPost(message)}
                          >
                            <img src={editIcon} alt="Edit" />
                          </IconButton>
                          <IconButton 
                            {...cursorHandlers}
                            onClick={() => handleDeletePost(message.id)}
                          >
                            <img src={trashIcon} alt="Delete" />
                          </IconButton>
                        </EditControls>
                      )}
                    </BlogMessageHeader>
                    <BlogMessageContent>{message.content}</BlogMessageContent>
                  </BlogMessageCard>
                </BlogMessageWrapper>
              ))}
            </BlogMessagesList>
          )}
        </BlogPageContent>
        <BackLink {...cursorHandlers} onClick={handleBackClick}>
          <BsChevronDoubleDown size={40} color={colors.red} />
        </BackLink>
        <Nav color={0} home={1} />
      </BlogPageContainer>
  );
}

export default BlogInstancePage;

